import { HttpClient, HttpHeaders } from "@angular/common/http";
import { Injectable } from "@angular/core";
import { BehaviorSubject, Observable } from "rxjs";
import { environment } from "src/environments/environment";

import { PaginationRequest } from "src/app/models/common/paginationRequest";
import { AppSessionService } from "../app-session/app-session.service";
import { FilterOperation } from "src/app/models/custom-table/custom-table.model";
import {
  setAdvancedFilterQuery,
  setSimpleFilterQuery,
} from "../../utils/tableUtils";

const EMPTY_STRING = "";
@Injectable({
  providedIn: "root",
})
export class CategoriesService {
  public remoteData: BehaviorSubject<any[]>;
  private BASE_URL = this.appSession.baseUrl + "/api/v1/categories";

  constructor(private http: HttpClient, private appSession: AppSessionService) {
    this.remoteData = new BehaviorSubject([]);
  }

  getCategories(pageReq: PaginationRequest): Observable<any> {
    let headers = new HttpHeaders();
    headers = headers.set(
      "Content-type",
      "application/x-www-form-urlencoded; "
    );

    if (pageReq && pageReq.query && (pageReq.query as string).length > 0) {
      headers = headers.set("query", pageReq.query);
    }

    const url = `${this.appSession.baseUrl + "/api/v1/categories"}`;
    if (!pageReq) {
      // pageReq not defined
      pageReq = {
        offset: 0,
        limit: 9999,
        realSize: false,
      };
    }

    return this.http.get(url, { headers });
  }

  getCategoriesByUsage(
    pageReq: PaginationRequest,
    usageCode: string
  ): Observable<any> {
    let headers = new HttpHeaders();
    headers = headers.set(
      "Content-type",
      "application/x-www-form-urlencoded; "
    );

    if (pageReq && pageReq.query && (pageReq.query as string).length > 0) {
      headers = headers.set("query", pageReq.query);
    }

    const url = `${
      this.appSession.baseUrl + "/api/v1/categories"
    }/elements?usageCode=${usageCode}`;

    if (!pageReq) {
      // pageReq not defined
      pageReq = {
        offset: 0,
        limit: undefined,
        realSize: false,
      };
    }

    const pagination =
      "?offset=" +
      pageReq.offset +
      (pageReq.limit ? "&limit=" + pageReq.limit : "") +
      "&real_size=" +
      pageReq.realSize +
      "&usageCode=" +
      (usageCode.length > 0 ? usageCode : undefined);

    // url = url.concat(pagination);

    return this.http.get(url, { headers });
  }
  createCategory(action): Observable<any> {
    const headers = new HttpHeaders({
      "Content-type": "application/json",
    });
    const data = JSON.stringify(action.category);
    const url = `${this.appSession.baseUrl + "/api/v1/categories"}`;
    return this.http.post(url, data, { headers });
  }

  updateCategory(category): Observable<any> {
    const headers = new HttpHeaders({
      "Content-type": "application/json",
    });

    const data = JSON.stringify(category);
    const url = `${this.appSession.baseUrl + "/api/v1/categories"}/${
      category.code
    }`;
    return this.http.put(url, data, { headers });
  }

  updateCategoryElement(category): Observable<any> {
    const headers = new HttpHeaders({
      "Content-type": "application/json",
    });

    const data = JSON.stringify(category);
    const url = `${this.appSession.baseUrl + "/api/v1/categories"}/${
      category.code
    }/elements`;
    return this.http.put(url, data, { headers });
  }

  deleteCategory(action): Observable<any> {
    const headers = new HttpHeaders({
      "Content-type": "application/json",
    });

    const url =
      `${this.appSession.baseUrl + "/api/v1/categories"}/` +
      encodeURIComponent(action.code);
    return this.http.delete(url, { headers });
  }
  getCategoriesElements(
    paging?: any,
    filteringArgs?: any,
    sortingArgs?: any,
    advancedFilteringArgs?: any
  ): Observable<any> {
    let headers = new HttpHeaders({
      "Content-type": "application/json",
    });

    let filterQuery = EMPTY_STRING;
    let advancedFilterQuery = EMPTY_STRING;
    let orderQuery = EMPTY_STRING;

    if (filteringArgs) {
      filterQuery = setSimpleFilterQuery(filteringArgs);
    }

    if (advancedFilteringArgs) {
      advancedFilterQuery = setAdvancedFilterQuery(advancedFilteringArgs);
    }

    if (sortingArgs) {
      const { active, direction } = sortingArgs;
      orderQuery += active + "," + direction.toUpperCase();
    }

    if (filterQuery.length > 0 || advancedFilterQuery.length > 0) {
      headers = headers.append(
        "query",
        `${filterQuery} ${
          filterQuery.length > 0 && advancedFilterQuery.length > 0
            ? FilterOperation.AND
            : ""
        } ${advancedFilterQuery}`
      );
    }

    if (orderQuery.length > 0) {
      headers = headers.append("sort", orderQuery);
    }

    return this.http.get(
      this.buildDataUrl(
        paging,
        filteringArgs,
        sortingArgs,
        headers,
        "/search?"
      ),
      { headers }
    );
  }

  getCategoriesElementsByCode(code: any): Observable<any> {
    const headers = new HttpHeaders({
      "Content-type": "application/json",
    });

    const url = `${
      this.appSession.baseUrl + "/api/v1/categories"
    }/${code}/elements`;
    return this.http.get(url, { headers });
  }

  getCategory(code: string): Observable<any> {
    const headers = new HttpHeaders({
      "Content-type": "application/json",
    });

    const url = `${this.appSession.baseUrl + "/api/v1/categories"}/${code}`;
    return this.http.get(url, { headers });
  }

  getCategoryUsages(pageReq: PaginationRequest): Observable<any> {
    let headers = new HttpHeaders();
    headers = headers.set(
      "Content-type",
      "application/x-www-form-urlencoded; "
    );

    if (pageReq && pageReq.query && (pageReq.query as string).length > 0) {
      headers = headers.set("query", pageReq.query);
    }

    const url = `${this.appSession.baseUrl + "/api/v1/categories"}/usages`;
    if (!pageReq) {
      // pageReq not defined
      pageReq = {
        offset: 0,
        limit: 9999,
        realSize: true,
      };
    }
    return this.http.get(url, { headers });
  }

  createCategoryElement(action): Observable<any> {
    const headers = new HttpHeaders({
      "Content-type": "application/json",
    });
    const data = JSON.stringify(action.categoryElements);
    const url =
      `${this.appSession.baseUrl + "/api/v1/categories"}/` +
      encodeURIComponent(action.categoryCode) +
      `/elements`;
    return this.http.post(url, data, { headers });
  }

  deleteCategoryElements(action): Observable<any> {
    const headers = new HttpHeaders({
      "Content-type": "application/json",
    });

    let data = "";
    if (action.categoryElements.length > 0) {
      action.categoryElements.forEach((categoryElement) => {
        if (data.length === 0) {
          data = data.concat("?categoryElementCode=" + categoryElement.code);
        } else {
          data = data.concat("&categoryElementCode=" + categoryElement.code);
        }
      });
    }

    const url =
      `${this.appSession.baseUrl + "/api/v1/categories"}/` +
      encodeURIComponent(action.categoryCode) +
      `/elements` +
      data;
    return this.http.request("delete", url, { headers });
  }
  private buildDataUrl(
    pagingArgs: any,
    filteringArgs: any,
    sortingArgs: any,
    header: HttpHeaders,
    path: string
  ): string {
    // let baseQueryString = `${DATA_URL}?$count=true`;
    let baseQueryString = `${
      this.appSession.baseUrl + "/api/v1/categories"
    }${path}`;
    let scrollingQuery = EMPTY_STRING;
    let query = EMPTY_STRING;

    if (pagingArgs) {
      scrollingQuery = this._buildScrollExpression(pagingArgs);
    }

    query +=
      scrollingQuery !== EMPTY_STRING ? `&${scrollingQuery}` : EMPTY_STRING;

    baseQueryString += query;

    return baseQueryString;
  }

  private _buildScrollExpression(pagingArgs): string {
    const skip = pagingArgs.skip;
    const top = pagingArgs.top;
    const realSize = pagingArgs.realSize;

    if (top && top !== null) {
      // return `real_size=true&offset=${skip}&limit=${top}`;
      return `real_size=true&offset=${skip}&limit=${top}`;
    } else {
      // return `real_size=true&offset=${skip}`;
      return `real_size=true&offset=${skip}`;
    }
  }
}
